import { BusinessCardData, UserProfile } from '@/types';

// Shape returned by the scan-unified API
interface ScanContactData {
  displayName?: string;
  givenName?: string;
  surname?: string;
  jobTitle?: string;
  companyName?: string;
  department?: string;
  emails?: Array<{ email: string; type: string; is_primary?: boolean }>;
  phones?: Array<{ number: string; type: string; is_primary?: boolean }>;
  addresses?: Array<{ street?: string; city?: string; state?: string; postalCode?: string; postal_code?: string; country?: string; type?: string }>;
  website?: string;
  socialProfiles?: Array<{ platform: string; url: string }>;
  notes?: string;
}

export interface ScanToCardResult {
  cardData: BusinessCardData;
  cardName: string;
  isFromScan: true;
}

export function mergeScanWithAzureAD(
  scanData: ScanContactData,
  azureProfile: UserProfile | null
): ScanToCardResult {
  const az = azureProfile;

  // Locked fields: always Azure AD
  const displayName = az?.displayName || scanData.displayName || '';
  const givenName = az?.givenName || scanData.givenName || '';
  const surname = az?.surname || scanData.surname || '';
  const mail = az?.mail || '';

  // Emails: scanned emails that differ from primary mail → additionalEmails
  const additionalEmails: string[] = [];
  if (scanData.emails) {
    for (const e of scanData.emails) {
      if (e.email && e.email.toLowerCase() !== mail.toLowerCase()) {
        additionalEmails.push(e.email);
      }
    }
  }

  // Phones: first scanned phone → mobilePhone (if no Azure AD mobile), rest → additionalPhones
  let mobilePhone = '';
  const additionalPhones: { type: string; number: string }[] = [];
  const scannedPhones = scanData.phones || [];
  for (const p of scannedPhones) {
    if (!p.number) continue;
    if (!mobilePhone) {
      mobilePhone = p.number;
    } else {
      additionalPhones.push({ type: p.type || 'WORK', number: p.number });
    }
  }

  // Address: use scanned if available
  const scannedAddresses = (scanData.addresses || []).map(a => ({
    type: a.type || 'WORK',
    street: a.street,
    city: a.city,
    state: a.state,
    postalCode: a.postalCode || a.postal_code,
    country: a.country,
  }));
  const address = scannedAddresses[0] || undefined;
  const additionalAddresses = scannedAddresses.slice(1);

  // Scanned-first fields
  const companyName = scanData.companyName || '';
  const department = scanData.department || '';
  const jobTitle = scanData.jobTitle || '';
  const website = scanData.website || '';
  const socialProfiles = scanData.socialProfiles || [];
  const notes = scanData.notes || '';

  // Auto-generate card name
  const cardName = companyName && jobTitle
    ? `${companyName} - ${jobTitle}`
    : companyName
      ? `${companyName} Card`
      : `${displayName}'s Card`;

  const cardData: BusinessCardData = {
    displayName,
    givenName,
    surname,
    mail,
    companyName,
    department,
    jobTitle,
    mobilePhone,
    businessPhones: [],
    additionalEmails,
    additionalPhones,
    address,
    additionalAddresses,
    website,
    socialProfiles,
    notes,
  };

  return { cardData, cardName, isFromScan: true };
}
